import { SPOTLIGHT_TARGETS, widgetOptionTarget } from "../spotlightTargets";
import { SPOTLIGHT_EVENTS, SPOTLIGHT_SIGNAL_SCOPES } from "../spotlightEventNames";
import { WIDGET_TYPE_OPTIONS } from "../../../constants/WidgetTypes";

const WIDGET_GUIDES = {
	Event: {
		intro: "Create an event your guests can RSVP to, with a date, location, description and reminders.",
		setup: "Press Create Event and follow the steps. Fill it in now, or come back to it later.",
	},
	PictureFrame: {
		intro: "Show off your favorite photos as tilted polaroid frames.",
		setup: "Use Add Photos to upload your pictures.",
	},
	ImageGallery: {
		intro: "Turn your photos into a slideshow carousel.",
		setup: "Use the upload controls to add photos to your slideshow.",
	},
	TextBox: {
		intro: "Add a personal message or any text you'd like to share.",
		setup: "Click inside the box and type your message.",
	},
	Poll: {
		intro: "Ask your guests a question and see what they pick.",
		setup: "Enter your question and answer options.",
	},
};

const PICKER_OR_ADD_TARGETS = [SPOTLIGHT_TARGETS.WIDGET_PICKER, SPOTLIGHT_TARGETS.WIDGET_ADD];

export const WIDGETS_INTRO_STEP_ID = "widgets-intro";
export const WIDGETS_COMPLETE_STEP_ID = "widgets-complete";

const getGuide = (option) => ({
	intro: option.desc,
	setup: "Fill in this widget's details.",
	...WIDGET_GUIDES[option.value],
});

const getStepIds = (option) => ({
	open: `widget-${option.value}-open`,
	intro: `widget-${option.value}-intro`,
	setup: `widget-${option.value}-setup`,
});

/** Widget types actually offered right now (the Poll option is feature-flagged); everything is assumed available until the page reports. */
const isOfferedWidget = (option, getSignals) => {
	const offered = getSignals?.(SPOTLIGHT_SIGNAL_SCOPES.ANNOUNCEMENT)?.availableWidgetTypes;
	return !offered || offered.split(",").includes(option.value);
};

const findNextOfferedOption = (index, getSignals) =>
	WIDGET_TYPE_OPTIONS.slice(index + 1).find((option) => isOfferedWidget(option, getSignals));

/**
 * One open/intro/setup trio per widget type in the real picker (WIDGET_TYPE_OPTIONS), so new widget types
 * join the tour automatically. Every trio can be skipped; after the last offered widget (created or skipped)
 * the tour continues at `stepAfterWidgets` and never asks for another widget.
 */
export const buildWidgetSteps = (stepAfterWidgets) =>
	WIDGET_TYPE_OPTIONS.flatMap((option, index) => {
		const ids = getStepIds(option);
		const nextIntroId = ({ getSignals }) => {
			const nextOption = findNextOfferedOption(index, getSignals);
			return nextOption ? getStepIds(nextOption).intro : stepAfterWidgets;
		};
		const nextOpenId = ({ getSignals }) => {
			const nextOption = findNextOfferedOption(index, getSignals);
			return nextOption ? getStepIds(nextOption).open : stepAfterWidgets;
		};
		const guide = getGuide(option);

		return [
			{
				id: ids.open,
				target: PICKER_OR_ADD_TARGETS,
				silent: true,
				interactive: true,
				optional: true,
				skipLabel: index === 0 ? "Skip widgets" : "Finish widgets",
				allowBack: false,
				instruction: "Open “Select a widget” (press + first to add a new widget)",
				skipTo: () => stepAfterWidgets,
				waitFor: [
					{ event: SPOTLIGHT_EVENTS.ANNOUNCEMENT_WIDGET_PICKER_OPENED, goto: ids.intro },
					{ event: SPOTLIGHT_EVENTS.ANNOUNCEMENT_WIDGET_ADDED, goto: ids.open },
				],
			},
			{
				id: ids.intro,
				target: widgetOptionTarget(option.value),
				interactive: true,
				optional: true,
				skipIfTargetMissing: true,
				skipLabel: "Skip this widget",
				allowBack: false,
				title: option.label,
				body: guide.intro,
				instruction: `Select ${option.label} to try it, or skip it`,
				skipTo: nextIntroId,
				waitFor: {
					event: SPOTLIGHT_EVENTS.ANNOUNCEMENT_WIDGET_SELECTED,
					when: ({ type }) => type === option.value,
					goto: ids.setup,
				},
			},
			{
				id: ids.setup,
				target: SPOTLIGHT_TARGETS.WIDGET_CARD,
				interactive: true,
				allowBack: false,
				title: `Set up your ${option.label}`,
				body: guide.setup,
				actionLabel: "Done",
				skipTo: nextOpenId,
			},
		];
	});
